import { initContract } from '@ts-rest/core'
import { z } from 'zod'
import { Brand } from '../../shared'
import { ErrorResponseSchema } from '../../shared/types'
import {
  ActivePhoenixPostcodesResponseSchema,
  BecoolZoneWithLocationsSchema,
  CancelShipmentResponseSchema,
  CheckServiceableInputSchema,
  CheckServiceableResponseSchema,
  CreateCourierEventInput,
  CreateCourierEventOutput,
  CreatePostcodeInputSchema,
  CreatePostcodeResponseSchema,
  CreateShipmentInputSchema,
  CreateShipmentResponse,
  CutoffDatesPathParams,
  CutoffDatesQueryInput,
  CutoffDatesResult,
  DeliveryScheduleQueryInput,
  DeliveryScheduleResult,
  PaginatedShipment,
  PostcodeFacilityResponse,
  PostcodeShippingZonesResponse,
  ResolveSuburbQuerySchema,
  SearchShipmentInput,
  Shipment,
  ShipmentStatusResponseSchema,
  ShipmentsUnmanifestedQueryInput,
  SuburbResolutionSchema,
  UnmanifestedShipmentsResponse,
  UpdateShipmentTrackingStatusInputSchema,
  UpdateShipmentTrackingStatusResponseSchema,
} from './schemas'

const c = initContract()

const options = {
  pathPrefix: '/api/v1/couriers/becool',
  commonResponses: {
    400: ErrorResponseSchema,
    404: ErrorResponseSchema,
    500: ErrorResponseSchema,
  },
}

export const courierServiceContract = c.router({
  createDelivery: {
    method: 'POST',
    path: '/shipments',
    body: CreateShipmentInputSchema,
    responses: {
      200: CreateShipmentResponse,
    },
  },
  findFacilityForPostcode: {
    method: 'GET',
    path: '/postcodes/:postcode/facility',
    pathParams: z.object({
      postcode: z.string(),
    }),
    query: z.object({
      brand: z.nativeEnum(Brand),
    }),
    responses: {
      200: PostcodeFacilityResponse,
    },
  },
  findShippingZoneForPostcode: {
    method: 'GET',
    path: '/postcodes/:postcode/shipping-zone',
    pathParams: z.object({
      postcode: z.string(),
    }),
    responses: {
      200: PostcodeShippingZonesResponse,
    },
  },
  CreatePostcode: {
    method: 'POST',
    path: '/admin/postcodes',
    body: CreatePostcodeInputSchema,
    responses: {
      201: CreatePostcodeResponseSchema,
    },
  },
  getActivePhoenixPostcodes: {
    method: 'GET',
    path: '/admin/postcodes/active',
    responses: {
      200: ActivePhoenixPostcodesResponseSchema,
    },
  },
  resolveSuburb: {
    method: 'GET',
    path: '/admin/postcodes/resolve-suburb',
    query: ResolveSuburbQuerySchema,
    responses: {
      200: SuburbResolutionSchema,
    },
    summary: 'Read-only: how a free-text suburb matches our shipping_postcodes rows',
  },
  getBecoolZoneByPostcode: {
    method: 'GET',
    path: '/admin/becool/zones/:postcode',
    pathParams: z.object({
      postcode: z.string(),
    }),
    responses: {
      200: BecoolZoneWithLocationsSchema,
    },
  },
  deleteManifest: {
    method: 'DELETE',
    path: '/shipments/:shipmentId',
    pathParams: z.object({
      shipmentId: z.string(),
    }),
    responses: {
      204: null,
    },
  },
  createCourierEvents: {
    method: 'POST',
    path: '/courier/events',
    body: CreateCourierEventInput,
    responses: {
      201: CreateCourierEventOutput,
    },
  },
  searchShipments: {
    method: 'GET',
    path: '/shipments/search',
    query: SearchShipmentInput,
    responses: {
      200: PaginatedShipment,
    },
  },
  getDeliverySchedules: {
    method: 'GET',
    path: '/delivery-schedules',
    query: DeliveryScheduleQueryInput,
    responses: {
      200: DeliveryScheduleResult,
    },
  },
  getCutoffDates: {
    method: 'GET',
    path: '/delivery-schedule/:postcode/:suburb/cutoffs',
    pathParams: CutoffDatesPathParams,
    query: CutoffDatesQueryInput,
    responses: {
      200: CutoffDatesResult,
    },
  },
  getShipmentById: {
    method: 'GET',
    path: '/shipments/:shipmentId',
    pathParams: z.object({
      shipmentId: z.string(),
    }),
    responses: {
      200: Shipment,
    },
  },
  getShipmentStatus: {
    method: 'GET',
    path: '/shipments/:shipmentId/status',
    pathParams: z.object({
      shipmentId: z.string(),
    }),
    responses: {
      200: ShipmentStatusResponseSchema,
    },
  },
  checkServiceability: {
    method: 'POST',
    path: '/serviceable',
    body: CheckServiceableInputSchema,
    responses: {
      200: CheckServiceableResponseSchema,
    },
  },
  getShipmentsUnmanifested: {
    method: 'GET',
    path: '/shipments/unmanifested',
    query: ShipmentsUnmanifestedQueryInput,
    responses: {
      200: UnmanifestedShipmentsResponse,
    },
  },
  updateShipmentTrackingStatus: {
    method: 'PATCH',
    path: '/shipments/:shipmentId/tracking-status',
    pathParams: z.object({
      shipmentId: z.string(),
    }),
    body: UpdateShipmentTrackingStatusInputSchema,
    responses: {
      200: UpdateShipmentTrackingStatusResponseSchema,
    },
  },
  cancelShipment: {
    method: 'POST',
    path: '/shipments/:shipmentId/cancel',
    pathParams: z.object({
      shipmentId: z.string(),
    }),
    body: null,
    responses: {
      200: CancelShipmentResponseSchema,
    },
  },
}, options)
